import React, { useState, useEffect } from 'react';
import { dailyDevotionals } from '../mock';
import { X, BookOpen, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DailyDevotionalPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [todaysDevotional, setTodaysDevotional] = useState(null);
  const [isSubscribing, setIsSubscribing] = useState(false);

  useEffect(() => {
    // Check if user has already seen today's devotional
    const today = new Date().toDateString();
    const hasSeenToday = localStorage.getItem(`devotional_seen_${today}`);
    
    if (!hasSeenToday) {
      // Get today's devotional (cycling through available ones)
      const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
      const devotionalIndex = dayOfYear % dailyDevotionals.length;
      setTodaysDevotional(dailyDevotionals[devotionalIndex]);
      
      // Show popup after 5 seconds
      const timer = setTimeout(() => {
        setShowPopup(true);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setShowPopup(false);
    // Mark as seen for today
    const today = new Date().toDateString();
    localStorage.setItem(`devotional_seen_${today}`, 'true');
  };

  const handleSubscribe = async () => {
    const email = prompt('Enter your email to receive daily devotionals:');
    if (!email) {
      handleClose();
      return;
    }

    setIsSubscribing(true);

    try {
      const response = await fetch(`${API}/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (response.ok) {
        alert('Thank you for subscribing! You\'ll receive daily devotionals in your email. Check your inbox for a welcome message.');
      } else {
        alert(result.error || 'Unable to subscribe right now. Please try again later or use the devotionals section below.');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Unable to subscribe right now. Please try the devotionals section below.');
    } finally {
      setIsSubscribing(false);
      handleClose();
    }
  };

  if (!showPopup || !todaysDevotional) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(139, 69, 19, 0.95)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'var(--bg-primary)',
        borderRadius: '0px',
        border: '2px solid var(--border-medium)',
        padding: '40px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Close Button */}
        <button
          onClick={handleClose}
          disabled={isSubscribing}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            cursor: isSubscribing ? 'not-allowed' : 'pointer',
            padding: '8px',
            borderRadius: '50%',
            transition: 'background 0.2s ease',
            opacity: isSubscribing ? 0.5 : 1
          }}
          onMouseEnter={(e) => !isSubscribing && (e.target.style.background = 'var(--bg-secondary)')}
          onMouseLeave={(e) => e.target.style.background = 'none'}
        >
          <X size={20} style={{ color: 'var(--text-secondary)' }} />
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
            <BookOpen size={24} style={{ color: 'var(--text-primary)' }} />
            <h3 className="heading-2">Today's Devotional</h3>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <Calendar size={16} style={{ color: 'var(--text-secondary)' }} />
            <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
              {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>
        </div>

        {/* Devotional Content */}
        <div style={{ marginBottom: '32px' }}>
          <h4 className="heading-3" style={{ marginBottom: '16px', textAlign: 'center' }}>
            {todaysDevotional.title}
          </h4>
          
          <div style={{ 
            background: 'var(--bg-secondary)', 
            padding: '16px', 
            borderRadius: '0px',
            marginBottom: '16px',
            borderLeft: '4px solid var(--text-primary)'
          }}>
            <p className="body-small" style={{ 
              color: 'var(--text-primary)', 
              fontStyle: 'italic',
              lineHeight: 1.6,
              textAlign: 'center'
            }}>
              {todaysDevotional.verse}
            </p>
          </div>
          
          <p className="body-regular" style={{ 
            color: 'var(--text-secondary)', 
            marginBottom: '16px',
            lineHeight: 1.7
          }}>
            {todaysDevotional.content}
          </p>
          
          <div style={{ 
            background: 'var(--bg-subtle)', 
            padding: '16px', 
            borderRadius: '0px'
          }}>
            <p className="body-small" style={{ 
              color: 'var(--text-primary)', 
              fontWeight: '600',
              marginBottom: '8px'
            }}>
              Prayer:
            </p>
            <p className="body-small" style={{ 
              color: 'var(--text-secondary)', 
              fontStyle: 'italic',
              lineHeight: 1.5
            }}>
              {todaysDevotional.prayer}
            </p>
          </div>
        </div>

        {/* Call to Action */}
        <div style={{ textAlign: 'center' }}>
          <p className="body-regular" style={{ 
            color: 'var(--text-secondary)', 
            marginBottom: '20px' 
          }}>
            Would you like to receive daily devotionals like this in your email?
          </p>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button 
              onClick={handleSubscribe}
              disabled={isSubscribing}
              className="btn-primary"
              style={{ 
                minWidth: 'auto', 
                padding: '12px 20px',
                opacity: isSubscribing ? 0.7 : 1,
                cursor: isSubscribing ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubscribing ? 'Subscribing...' : 'Yes, Subscribe'}
            </button>
            
            <button 
              onClick={handleClose}
              disabled={isSubscribing}
              className="btn-secondary"
              style={{ 
                minWidth: 'auto',
                opacity: isSubscribing ? 0.7 : 1,
                cursor: isSubscribing ? 'not-allowed' : 'pointer'
              }}
            >
              Maybe Later
            </button>
          </div>
          
          <p className="body-small" style={{ 
            color: 'var(--text-light)', 
            marginTop: '12px',
            lineHeight: 1.4
          }}>
            Free daily devotionals delivered to your inbox every morning
          </p>
        </div>
      </div>
    </div>
  );
};